import { useStore } from '@tanstack/react-form';
import { Divider, Input, LearnMoreLink, Modal, RadioSet, Text } from '@ui/themed';
import { t } from 'i18next';
import { SettingToggleRow } from '../../../../components/setting-toggle-row';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import { buildOAuthAuthorizeUrl } from '../../oauth';
import { getOAuthClientIds } from '../../oauth-client-ids';
import type { PartnerModalProps } from '../../types';
import { SectionTitle } from '../section-title';
import { buildOAuthRedirectUri, generateOAuthState, OAUTH, usePartnerModalSave } from './shared';

const XERO = 'features:partners.components.xero' satisfies I18nPrefix;
const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

const XERO_SCOPES = [
  'offline_access',
  'accounting.transactions',
  'accounting.contacts',
  'accounting.settings.read',
].join(' ');

function startOAuthFlow() {
  const state = generateOAuthState();
  localStorage.setItem(OAUTH.xero.stateKey, state);
  window.location.href = buildOAuthAuthorizeUrl(
    'https://login.xero.com/identity/connect/authorize',
    {
      response_type: 'code',
      client_id: getOAuthClientIds().xero,
      redirect_uri: buildOAuthRedirectUri(OAUTH.xero.redirectPath),
      scope: XERO_SCOPES,
      state,
    },
  );
}

export function XeroModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasOAuthCode = !!settings.integrationXeroOAuthCode;

  // Settings the API has never persisted come back `undefined`, so every boolean needs a fallback
  // before it reaches a controlled input or the PATCH payload.
  const form = useAppForm({
    defaultValues: {
      ignoreTransactions: settings.ignoreTransactions ?? false,
      autoContactCreationEnabled: settings.integrationXeroAutoContactCreationEnabled ?? false,
      defaultLineItemAccountCode: settings.integrationXeroDefaultLineItemAccountCode ?? '',
      shippingLineAccountCode: settings.integrationXeroShippingLineAccountCode ?? '',
      paymentOnAccountInvoiceOn: (settings.integrationXeroPaymentOnAccountInvoiceOn ??
        'purchaseCreated') as 'purchaseCreated' | 'packageShipped',
      invoicesUseItemCodes: settings.integrationXeroInvoicesUseItemCodes ?? false,
      lineItemTracking: settings.integrationXeroLineItemTracking ?? '',
    },
  });

  const values = useStore(form.store, (state) => state.values);

  function getSelectedSettings(): Setting[] {
    return [
      { key: 'integrationXero', value: true },
      {
        key: 'integrationXeroAutoContactCreationEnabled',
        value: values.autoContactCreationEnabled,
      },
      {
        key: 'integrationXeroDefaultLineItemAccountCode',
        value: values.defaultLineItemAccountCode,
      },
      { key: 'integrationXeroShippingLineAccountCode', value: values.shippingLineAccountCode },
      { key: 'integrationXeroPaymentOnAccountInvoiceOn', value: values.paymentOnAccountInvoiceOn },
      { key: 'integrationXeroInvoicesUseItemCodes', value: values.invoicesUseItemCodes },
      { key: 'integrationXeroLineItemTracking', value: values.lineItemTracking },
      { key: 'ignoreTransactions', value: values.ignoreTransactions },
    ];
  }

  function handleSave() {
    void save(getSelectedSettings());
  }

  // Xero has nowhere to stash the form while the user is away at the provider, so the settings are
  // persisted first and the redirect only happens once that succeeds.
  function handleStartOAuthFlow() {
    void save(getSelectedSettings(), startOAuthFlow);
  }

  return (
    <Modal
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={
        !hasOAuthCode
          ? {
              text: t(`${COMMON}.authorise_access`, { partner: 'Xero' }),
              onClick: handleStartOAuthFlow,
              loading: isSaving,
            }
          : { text: t(`${COMMON}.save`), onClick: handleSave, loading: isSaving }
      }
    >
      <Modal.Header>Xero</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <Text variant="secondary">
            {t(`${XERO}.description`)} <LearnMoreLink link={PARTNER_DOCS_URLS.xero} />
          </Text>

          <SettingToggleRow
            label={t(`${XERO}.take_payment.label`)}
            description={t(`${XERO}.take_payment.description`)}
            checked={values.ignoreTransactions}
            onChange={(checked) => form.setFieldValue('ignoreTransactions', checked)}
            learnMoreUrl={PARTNER_DOCS_URLS.xero}
          />

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.xero}>{t(`${XERO}.contacts.title`)}</SectionTitle>
            <Text variant="secondary">
              {t(`${XERO}.contacts.description`)}{' '}
              <LearnMoreLink link={`${PARTNER_DOCS_URLS.xero}#contact-matching-creation`} />
            </Text>
            <RadioSet<'enabled' | 'disabled'>
              label={t(`${XERO}.auto_contact_creation.label`)}
              name="integrationXeroAutoContactCreationEnabled"
              value={values.autoContactCreationEnabled ? 'enabled' : 'disabled'}
              onChange={(selected) =>
                form.setFieldValue('autoContactCreationEnabled', selected === 'enabled')
              }
              options={[
                {
                  label: t(`${XERO}.auto_contact_creation.enabled.label`),
                  description: t(`${XERO}.auto_contact_creation.enabled.description`),
                  value: 'enabled',
                },
                {
                  label: t(`${XERO}.auto_contact_creation.disabled.label`),
                  description: t(`${XERO}.auto_contact_creation.disabled.description`),
                  value: 'disabled',
                },
              ]}
            />
          </div>

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.xero}>{t(`${XERO}.invoices.title`)}</SectionTitle>
            <form.Field name="defaultLineItemAccountCode">
              {(field) => (
                <Input
                  label={t(`${XERO}.invoices.default_line_item_account_code.label`)}
                  description={t(`${XERO}.invoices.default_line_item_account_code.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
            <form.Field name="shippingLineAccountCode">
              {(field) => (
                <Input
                  label={t(`${XERO}.invoices.shipping_line_account_code.label`)}
                  description={t(`${XERO}.invoices.shipping_line_account_code.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
            <RadioSet<'purchaseCreated' | 'packageShipped'>
              label={t(`${XERO}.invoices.payment_on_account.label`)}
              name="integrationXeroPaymentOnAccountInvoiceOn"
              value={values.paymentOnAccountInvoiceOn}
              onChange={(selected) => form.setFieldValue('paymentOnAccountInvoiceOn', selected)}
              options={[
                {
                  label: t(`${XERO}.invoices.payment_on_account.on_order.label`),
                  description: t(`${XERO}.invoices.payment_on_account.on_order.description`),
                  value: 'purchaseCreated',
                },
                {
                  label: t(`${XERO}.invoices.payment_on_account.on_shipment.label`),
                  description: t(`${XERO}.invoices.payment_on_account.on_shipment.description`),
                  value: 'packageShipped',
                },
              ]}
            />
          </div>

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.xero}>
              {t(`${XERO}.line_item_mapping.title`)}
            </SectionTitle>
            <SettingToggleRow
              label={t(`${XERO}.line_item_mapping.use_item_codes.label`)}
              description={t(`${XERO}.line_item_mapping.use_item_codes.description`)}
              checked={values.invoicesUseItemCodes}
              onChange={(checked) => form.setFieldValue('invoicesUseItemCodes', checked)}
              learnMoreUrl={PARTNER_DOCS_URLS.xero}
            />
            <form.Field name="lineItemTracking">
              {(field) => (
                <Input
                  label={t(`${XERO}.line_item_mapping.tracking.label`)}
                  description={t(`${XERO}.line_item_mapping.tracking.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
